import { ApiError } from '../../api/client';

/**
 * The silent onboarding self-link claim (steward-app#1851).
 *
 * A firm sets up a household for a client it onboarded, and the client prefill creates a
 * `person` row for the client, stamped `onboardingSubject: client`. When that client signs in
 * to the household, this asks appcore to mark that row as theirs
 * (`POST /people/link-self/from-onboarding`, body `{ firmWorkspaceId }`). The request names no
 * person and no user: appcore finds the stamped row and writes the caller's own user id.
 * Nothing is shown, whatever the answer.
 *
 * ── When a pair is finished ───────────────────────────────────────────────────
 * Two different kinds of "finished", and the difference matters:
 *
 *   - **For this page load** (`settled`, in memory): any answer except `STEP_UP_REQUIRED`.
 *     So one mounted hook posts at most once per (workspace, firm) for a 404, a 409, a 422,
 *     a 429 or a network failure, and never loops. The set lives in the hook instance; it is
 *     "per page load" only because `Layout` mounts the hook once, which nothing here pins.
 *   - **For good** (the `/me` preference `onboardingSelfLinked`): a 200, or
 *     409 `SELF_RECORD_TAKEN` (the caller already marked a different record as theirs, which
 *     is their answer). Also written by the Family page when a person clears their own link
 *     (`<workspaceId>#*`), so a link someone cleared is not put back at the next sign-in.
 *     Server-side for the reason `onboardingTaxIdCopied` gives: localStorage is per browser.
 *
 * `STEP_UP_REQUIRED` finishes nothing. appcore runs this route through the tax-id copy's
 * step-up gate (reveal tier in an unfloored household, view tier in a floored one), so on
 * entering an unfloored household the claim is normally refused for the tier. It is tried
 * again after the next successful step-up ceremony, which is also when the tax-id copy
 * lands. The first `STEP_UP_REQUIRED` ends the run: every later firm would meet the same gate.
 *
 * Not finished for good, on purpose: 404 (the prefill may not have written the row yet) and
 * 409 `PERSON_LINKED_TO_ANOTHER` (the holder may clear it). Each costs one request per page
 * load per firm.
 *
 * The gap: a 200 whose preference write fails, followed by the person clearing the link on a
 * device that also fails to write `#*`. Then the claim lands once more.
 */

export const SELF_LINKED_PREFERENCE = 'onboardingSelfLinked';
export const SELF_LINKED_MAX_ENTRIES = 50;

export function selfLinkedKey(workspaceId: string, firmWorkspaceId: string): string {
  return `${workspaceId}#${firmWorkspaceId}`;
}
/** The entry that settles every firm for a workspace: the person cleared their own link. */
export function selfLinkDeclinedKey(workspaceId: string): string {
  return `${workspaceId}#*`;
}

/** The settled list with `added` appended, deduplicated and capped to the newest entries.
 *  Narrowed, not cast: a non-array preference would make `.includes` a substring match. */
export function mergeSelfLinked(existing: unknown, added: readonly string[]): string[] {
  const prior = Array.isArray(existing) ? existing.filter((x): x is string => typeof x === 'string') : [];
  const out = prior.filter(k => !added.includes(k));
  out.push(...added);
  return out.slice(-SELF_LINKED_MAX_ENTRIES);
}

export interface SelfLinkClaimDeps {
  workspaceId: string;
  firmWorkspaceIds: readonly string[];
  /** The current `onboardingSelfLinked` list. */
  done: readonly string[];
  /** Pairs already answered this page load. Mutated: every answered pair is added. */
  settled: Set<string>;
  claim: (firmWorkspaceId: string) => Promise<{ already?: boolean }>;
  markDone: (keys: string[]) => Promise<void>;
  log: (event: string, fields: Record<string, string | number>) => void;
}

export interface SelfLinkClaimResult {
  /** Claims that newly linked a record (a 200 with `already` false). */
  linked: number;
  markedDone: string[];
}

function codeOf(err: unknown): string {
  return err instanceof ApiError ? `${err.status}:${err.code}` : 'network';
}

/** Never rejects: every failure is logged (a code, never a value) and dropped. */
export async function runSelfLinkClaim(deps: SelfLinkClaimDeps): Promise<SelfLinkClaimResult> {
  const result: SelfLinkClaimResult = { linked: 0, markedDone: [] };
  if (deps.done.includes(selfLinkDeclinedKey(deps.workspaceId))) return result;
  const newlyDone: string[] = [];
  try {
    for (const fw of [...new Set(deps.firmWorkspaceIds)]) {
      const key = selfLinkedKey(deps.workspaceId, fw);
      if (deps.done.includes(key) || deps.settled.has(key)) continue;
      try {
        const res = await deps.claim(fw);
        deps.settled.add(key);
        newlyDone.push(key);
        if (res?.already !== true) result.linked += 1;
        deps.log('linked', { already: res?.already === true ? 1 : 0 });
      } catch (err) {
        if (err instanceof ApiError && err.code === 'STEP_UP_REQUIRED') {
          deps.log('claim-deferred', { code: codeOf(err) });
          return result;
        }
        deps.settled.add(key);
        if (err instanceof ApiError && err.status === 409 && err.code === 'SELF_RECORD_TAKEN') newlyDone.push(key);
        deps.log('claim-refused', { code: codeOf(err) });
      }
    }
    return result;
  } finally {
    if (newlyDone.length > 0) {
      try {
        await deps.markDone(newlyDone);
        result.markedDone = newlyDone;
      } catch (err) {
        deps.log('mark-done-failed', { code: codeOf(err) });
      }
    }
  }
}
